<template>
  <!-- Mobile View -->
  <input
    ref="coverFileInput"
    type="file"
    accept="image/*"
    class="hidden"
    @change="onCoverSelected"
  >

  <QuizDetailMobile
    v-if="isMobile"
    :quiz="quiz"
    :quiz-id="quizId"
    :quiz-list="quizList"
    :leaderboard="leaderboard"
    :show-upgrade-banner="showUpgradeBanner"
    :go-subscribe="goSubscribe"
    :go-to-quiz="goToQuiz"
    :is-login="auth.isLoggedIn"
    :is-saved="isSaved"
    :saving="saving"
    :sharing="sharing"
    :toggle-saved="toggleSaved"
    :share-quiz="shareQuiz"
    :can-archive="canArchive"
    :toggle-archived="toggleArchived"
    :is-archived="isArchived"
    :archiving="archiving"
    :can-edit-cover="canEditCover"
    :updating-cover="updatingCover"
    @edit-cover="triggerCoverUpload"
  />

  <!-- Desktop View -->
  <QuizDetailSidebarMobile
    v-else
    :quiz="quiz"
    :quiz-id="quizId"
    :quiz-list="quizList"
    :leaderboard="leaderboard"
    :selected-menu="selectedMenu"
    :show-upgrade-banner="showUpgradeBanner"
    :quiz-loading="quizLoading"
    :go-subscribe="goSubscribe"
    :go-to-quiz="goToQuiz"
    :is-login="auth.isLoggedIn"
    :is-saved="isSaved"
    :saving="saving"
    :sharing="sharing"
    :toggle-saved="toggleSaved"
    :share-quiz="shareQuiz"
    :can-archive="canArchive"
    :toggle-archived="toggleArchived"
    :is-archived="isArchived"
    :archiving="archiving"
    :can-edit-cover="canEditCover"
    :updating-cover="updatingCover"
    @edit-cover="triggerCoverUpload"
  />

  <!-- Toast Notification -->
  <transition name="fade">
    <div
      v-if="toast.show"
      class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-black/80 text-white text-sm px-3 py-2 rounded-lg z-50"
    >
      {{ toast.message }}
    </div>
  </transition>
</template>

<script setup>
import { useRoute, useRouter } from "vue-router";
import { onMounted, ref, onBeforeUnmount, reactive, computed } from "vue";
import QuizDetailMobile from "@/components/detailQuiz/QuizDetailMobile.vue";
// import QuizDetailDesktop from "@/components/detailQuiz/QuizDetailDesktop.vue";
import {
  QuizPreview,
  AllQuizList,
  SaveQuizToggle,
  CheckDailyLimitByStudent,
  ArchiveQuizToggle,
  UpdateQuizCoverImage,
} from "@/api/quizApi";
import { useAuthStore } from "@/stores/authStore";
import {
  GlobalAllTimeLeaderboard,
  LeaderboardByUserId,
} from "@/api/leaderboardApi";
import QuizDetailSidebarMobile from "@/components/detailQuiz/QuizDetailSidebarMobile.vue";
import { useSnackbarStore } from "@/stores/snackbarStore";

const auth = useAuthStore();
const snackbar = useSnackbarStore();
const router = useRouter();

const quiz = ref(null);
const coverFileInput = ref(null);
const updatingCover = ref(false);

const selectedMenu = ref("Detail");
const route = useRoute();
const quizId = route.params.id;

const params = { guid: quizId };

const goToQuiz = (item) => {
  const id = item?.id;
  if (id != null) window.location.assign(`/quiz/${id}`);
};

/* ===== Responsive Detection ===== */
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024; // lg breakpoint
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

/* ===== Share & Save ===== */
const sharing = ref(false);
const saving = ref(false);
const isSaved = ref(false);
const archiving = ref(false);
const isArchived = ref(false);
const savedKey = "savedQuizzes";

function initSaved() {
  try {
    const arr = JSON.parse(localStorage.getItem(savedKey) || "[]");
    isSaved.value = arr.includes(String(quizId));
  } catch {
    isSaved.value = false;
  }
}
function persistSaved(val) {
  let arr = [];
  try {
    arr = JSON.parse(localStorage.getItem(savedKey) || "[]");
  } catch {
    arr = [];
  }
  const set = new Set(arr.map(String));
  if (val) set.add(String(quizId));
  else set.delete(String(quizId));
  localStorage.setItem(savedKey, JSON.stringify([...set]));
}

const normalizeArchivedFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["1", "true", "yes", "y"].includes(normalized);
  }
  return Boolean(value);
};

const canArchive = computed(() => {
  const quizData = quiz.value;
  if (!quizData) return false;

  const ownerFlag =
    quizData?.isOwner ??
    quizData?.IsOwner ??
    quizData?.isOwned ??
    quizData?.Owned ??
    false;

  const creatorId =
    quizData?.createdBy ??
    quizData?.CreatedBy ??
    quizData?.creatorId ??
    quizData?.CreatorId ??
    null;

  const matchesUser =
    creatorId != null &&
    auth?.userId != null &&
    String(creatorId) === String(auth.userId);

  return Boolean(ownerFlag || matchesUser);
});
const canEditCover = computed(() => canArchive.value);

const pickImageUrl = (payload) => {
  const buckets = [payload, payload?.data];
  const keys = [
    "imagePath",
    "ImagePath",
    "image",
    "Image",
    "coverImage",
    "CoverImage",
    "cover",
    "Cover",
  ];

  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== "object") continue;
    for (const key of keys) {
      const value = bucket[key];
      if (value) return value;
    }
  }
  return "";
};

const triggerCoverUpload = () => {
  if (!canEditCover.value) {
    showToast("Hanya pemilik yang dapat mengubah cover");
    return;
  }
  coverFileInput.value?.click();
};

const onCoverSelected = async (event) => {
  const file = event.target?.files?.[0];
  if (!file) return;
  if (!auth?.userId) {
    showToast("Masuk terlebih dahulu");
    return;
  }
  const guid = quiz.value?.id ?? quizId;
  if (!guid) {
    showToast("Quiz belum siap");
    return;
  }

  updatingCover.value = true;
  try {
    const response = await UpdateQuizCoverImage({
      quizGuid: guid,
      userId: auth.userId,
      coverImage: file,
    });
    const newUrl = pickImageUrl(response) || URL.createObjectURL(file);
    quiz.value = {
      ...(quiz.value || {}),
      image: newUrl,
      imagePath: newUrl,
    };
    showToast("Cover berhasil diubah");
    snackbar.trigger("Cover berhasil diubah", "success");
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }, 600);
  } catch (error) {
    console.error("Update quiz cover failed:", error);
    showToast("Gagal mengubah cover");
  } finally {
    updatingCover.value = false;
    if (coverFileInput.value) coverFileInput.value.value = "";
  }
};
async function toggleSaved() {
  if (saving.value) return;

  const next = !isSaved.value;
  // Optimistic update + local persistence
  isSaved.value = next;
  persistSaved(next);

  if (!auth?.isLoggedIn || !auth?.userId) {
    showToast(next ? "Saved locally" : "Removed locally");
    return;
  }

  saving.value = true;
  try {
    await SaveQuizToggle({
      guid: quizId,
      studentId: auth.userId,
      isSaved: next,
    });
    showToast(next ? "Saved" : "Removed");
  } catch (e) {
    isSaved.value = !next;
    persistSaved(isSaved.value);
    console.error("SaveQuiz toggle failed:", e?.message || e);
    showToast("Failed to update. Please try again");
  } finally {
    saving.value = false;
  }
}

async function toggleArchived() {
  if (!canArchive.value || archiving.value) return;
  const next = !isArchived.value;
  isArchived.value = next;
  archiving.value = true;
  try {
    await ArchiveQuizToggle({
      guid: quizId,
      isArchived: next,
      userId: auth.userId,
    });
    showToast(next ? "Quiz archived" : "Quiz restored");
    if (next) {
      quizList.value = quizList.value.filter(
        (item) => String(item.id ?? item.guid ?? item.Guid) !== String(quizId)
      );
    }
  } catch (e) {
    isArchived.value = !next;
    console.error("Archive toggle failed:", e?.message || e);
    showToast("Failed to update archive status");
  } finally {
    archiving.value = false;
  }
}
async function shareQuiz() {
  if (sharing.value) return;

  const resolved = router.resolve({
    name: "QuizDetail",
    params: { id: quizId },
  });
  const url = `${window.location.origin}${resolved.href}`;
  const title = quiz.value?.title || "Quiz";
  const text = `Check out this quiz: ${title}`;

  sharing.value = true;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      showToast("Shared!");
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      showToast("Link copied");
    } else {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      showToast("Link copied");
    }
  } catch (error) {
    if (error?.name !== "AbortError") {
      console.error("Share failed:", error);
      showToast("Unable to share right now");
    }
  } finally {
    sharing.value = false;
  }
}
/* ===== Upgrade Banner (limit harian dari backend) ===== */
const showUpgradeBanner = ref(false);
const limitInfo = ref(null);
const savedAuthUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("auth_user") || "null");
  } catch {
    return null;
  }
})();
const studentId = computed(
  () =>
    auth?.userId ||
    auth?.user?.userId ||
    auth?.user?.data?.userId ||
    savedAuthUser?.data?.userId ||
    savedAuthUser?.userId ||
    null
);

async function checkDailyLimit() {
  try {
    if (!studentId.value) {
      showUpgradeBanner.value = false;
      return;
    }
    const data = await CheckDailyLimitByStudent(studentId.value);
    showUpgradeBanner.value = !!data?.limitReached;
    limitInfo.value = data || null;
  } catch (e) {
    console.warn("checkDailyLimit failed:", e?.message || e);
    showUpgradeBanner.value = false;
  }
}
function goSubscribe() {
  router.push("/subscribe");
}

/* ===== Toast helper ===== */
const toast = reactive({ show: false, message: "" });
let toastTimer;
function showToast(message) {
  toast.message = message;
  toast.show = true;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.show = false;
  }, 1800);
}

/* ===== Fetch Quiz Detail ===== */
onMounted(async () => {
  initSaved();

  try {
    const response = await QuizPreview(params);
    quiz.value = response;

    // If backend returns saved state, sync it
    if (typeof response?.isSaved !== "undefined") {
      isSaved.value = !!response.isSaved;
      persistSaved(isSaved.value);
    }

    const archivedFlag =
      response?.isArchived ??
      response?.IsArchived ??
      response?.archived ??
      response?.Archived ??
      null;
    if (archivedFlag != null) {
      isArchived.value = normalizeArchivedFlag(archivedFlag);
    }

    const nextQuery = { ...route.query };
    if (response?.id) {
      nextQuery.quizGuid = String(response.id);
    } else {
      delete nextQuery.quizGuid;
    }
    if (response?.requireReviewPin) {
      nextQuery.requireReviewPin = "1";
    } else {
      delete nextQuery.requireReviewPin;
    }
    const guidChanged = nextQuery.quizGuid !== route.query.quizGuid;
    const pinFlagChanged =
      (nextQuery.requireReviewPin ?? undefined) !==
      (route.query.requireReviewPin ?? undefined);
    if (guidChanged || pinFlagChanged) {
      router.replace({ path: route.path, query: nextQuery });
    }
    await fetchQuizList();
  } catch (err) {
    console.error("Gagal ambil quiz:", err.message);
  }

  // Banner premium – tampil bila limit harian terpenuhi (dari backend)
  checkDailyLimit();

  // Leaderboard
  fetchLeaderboard();
  fetchLeaderboardById();
});

/* ===== Achievements (static images) ===== */
// const badges = [
//   { label: "Math Champion", img: require("@/assets/Badge1.png") },
//   { label: "Science Star", img: require("@/assets/Badge2.png") },
// ];

/* ===== Quiz List (child quizzes) ===== */
const toNum = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const quizList = ref([]);
const quizPage = ref(1);
const quizPageSize = ref(10);
const quizTotalPages = ref(1);
const quizLoading = ref(false);

const fetchQuizList = async () => {
  if (quizLoading.value || quizPage.value > quizTotalPages.value) return;
  quizLoading.value = true;

  try {
    const data = await AllQuizList({
      parentId: quiz.value?.id,
      page: quizPage.value,
      pageSize: quizPageSize.value,
      userId: auth?.userId,
      quizSubjectIds: [quiz.value?.subjectId]
        .map(toNum)
        .filter((n) => n != null),
    });

    quizList.value.push(...data.data.filter((q) => q.totalQuiz > 0));
    quizTotalPages.value = data.totalPages;
    quizPage.value++;
  } catch (err) {
    console.error("Gagal load daftar quiz:", err.message);
  } finally {
    quizLoading.value = false;
  }
};

// Infinite scroll hanya saat tab "Quiz"
const handleScroll = () => {
  const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
  if (
    scrollTop + clientHeight >= scrollHeight - 50 &&
    selectedMenu.value === "Quiz"
  ) {
    fetchQuizList();
  }
};
onMounted(() => window.addEventListener("scroll", handleScroll));
onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  clearTimeout(toastTimer);
});

/* ===== Leaderboard (API) ===== */
const leaderboard = ref([]);
const leaderboardScore = ref([]);

function getBadge(rank) {
  if (rank === 1) return require("@/assets/leaderboard-1.png");
  if (rank === 2) return require("@/assets/leaderboard-2.png");
  if (rank === 3) return require("@/assets/leaderboard-3.png");
  return require("@/assets/leaderboard-3.png");
}
const fetchLeaderboard = async () => {
  try {
    const data = await GlobalAllTimeLeaderboard({ top: 10, quizGuid: quizId });
    leaderboard.value = data.map((user, index) => ({
      name: user.Name,
      xp: user.XP,
      avatar: require("@/assets/Rectangle.png"),
      badge: getBadge(index + 1),
    }));
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
    leaderboard.value = []; // Set empty array on error
  }
};
const fetchLeaderboardById = async () => {
  try {
    const data = await LeaderboardByUserId(auth.userId, { quizGuid: quizId });
    leaderboardScore.value = data;
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
